/**
 * Shared parameter & hardware utilities for all model template stores.
 * Extracted from per-model reducer utilities to avoid duplication.
 */

export interface Position {
  x: number
  y: number
  z: number
}

export interface HardwareItem {
  positionId: string
  type: string
  width: number
  height: number
  thickness: number
  color: string
  material?: string
  materialId?: string | number | null
  materialName?: string
  position: Position
}

export interface Hardware {
  handles?: HardwareItem[]
  hinges?: HardwareItem[]
  locks?: HardwareItem[]
  [key: string]: HardwareItem[] | undefined
}

export interface ModelParameter {
  positionId: string
  type: 'PROFILE' | 'GLASS' | string
  width: number
  height: number
  thickness: number
  color: string
  material?: string
  materialId?: string | number | null
  materialName?: string
  isDouble?: boolean | null
  comment?: string | null
  position: Position
  hardware?: Hardware | null
}

export interface DoorConfiguration {
  type: string
  totalDoorWidth: number
  individualDoorWidth: number[]
  totalHandles: number
  openingDirection: string
  hingePosition?: string
  leftDoorWidth?: number
  rightDoorWidth?: number
  leftFixedWidth?: number
  rightFixedWidth?: number
  centerDoorWidth?: number
  sections?: Record<string, any> | Record<string, any>[]
  [key: string]: unknown
}

export interface ModelData {
  name: string | null
  modelId: string | null
  type: string | null
  description: string | null
  width?: number
  height?: number
  photoId?: string | number | null
  attachments?: Record<string, any>[]
  serviceNames?: string[]
  serviceIds?: (number | null)[]
  parameters?: ModelParameter[]
  doorConfiguration?: DoorConfiguration
  [key: string]: unknown
}

/** Partial update type that allows partial Position in position field */
export type ModelParameterUpdate = Partial<Omit<ModelParameter, 'position'>> & {
  position?: Partial<Position>
}

// ─── Parameter utilities ───

export const parameterUtils = {
  findParameter: (parameters: ModelParameter[], positionId: string): ModelParameter | undefined =>
    parameters.find((param) => param.positionId === positionId),

  findParameterIndex: (parameters: ModelParameter[], positionId: string): number =>
    parameters.findIndex((param) => param.positionId === positionId),

  updateParameterInArray: (
    parameters: ModelParameter[],
    positionId: string,
    updates: ModelParameterUpdate,
  ): ModelParameter[] =>
    parameters.map((param) => {
      if (param.positionId === positionId) {
        const updatedParam = { ...param, ...updates }

        if (param.position && updates.position) {
          updatedParam.position = {
            x: updates.position.x !== undefined ? updates.position.x : param.position.x,
            y: updates.position.y !== undefined ? updates.position.y : param.position.y,
            z: updates.position.z !== undefined ? updates.position.z : param.position.z,
          }
        } else if (updates.position) {
          updatedParam.position = { ...updates.position }
        }

        if (param.hardware && updates.hardware) {
          updatedParam.hardware = { ...param.hardware, ...updates.hardware }
        }

        return updatedParam as ModelParameter
      }
      return param
    }),

  updateParametersByType: (
    parameters: ModelParameter[],
    type: string,
    updates: ModelParameterUpdate,
    excludePositionId: string | null = null,
  ): ModelParameter[] =>
    parameters.map((param) => {
      if (param.type === type && param.positionId !== excludePositionId) {
        return { ...param, ...updates } as ModelParameter
      }
      return param
    }),
}

// ─── Hardware utilities ───

export const hardwareUtils = {
  updateHardwareInParameter: (
    parameter: ModelParameter,
    hardwareType: string,
    hardwarePositionId: string,
    updates: Partial<HardwareItem>,
  ): ModelParameter => {
    if (!parameter.hardware?.[hardwareType]) {
      return parameter
    }

    const updatedHardwareArray = parameter.hardware[hardwareType]!.map((item) =>
      item.positionId === hardwarePositionId ? { ...item, ...updates } : item,
    )

    return {
      ...parameter,
      hardware: {
        ...parameter.hardware,
        [hardwareType]: updatedHardwareArray,
      },
    }
  },

  getHardwareTypeFromElement: (
    parameters: ModelParameter[],
    hardwarePositionId: string,
  ): { hardwareType: string | null; elementType: string | null } => {
    for (const param of parameters) {
      if (param.hardware) {
        for (const [hardwareType, hardwareArray] of Object.entries(param.hardware)) {
          if (!hardwareArray) continue
          const hardware = hardwareArray.find((hw) => hw.positionId === hardwarePositionId)
          if (hardware) {
            return { hardwareType, elementType: hardware.type }
          }
        }
      }
    }
    return { hardwareType: null, elementType: null }
  },
}
